import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import DiscountCodeInput from '../components/DiscountCodeInput';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { orderService } from '../services/orderService';
import { formatPrice } from '../utils/apiHelpers';
import { trackBeginCheckout } from '../utils/analytics';
import { PaymentStep } from '../components/payment';
import stripeService from '../services/stripeService';

function Checkout() {
  const navigate = useNavigate();
  const { cart, clearCart, isEmpty, subtotal, itemCount, fetchCart } = useContext(CartContext);
  const { isAuthenticated, user } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // State for PaymentIntent flow
  const [createdOrder, setCreatedOrder] = useState(null);

  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'UK'
  });

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('stripe_card');
  const [billingAddressSame, setBillingAddressSame] = useState(true);

  // Discount state
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Gift order state
  const [isGift, setIsGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const [giftMessageError, setGiftMessageError] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  // Ref to track if initial load from localStorage is complete
  const initialLoadComplete = useRef(false);

  // Redirect to cart if cart is empty (but not if we're in payment step)
  useEffect(() => {
    // Don't redirect if we're at step 4 (payment step) - order is already created
    if (isEmpty && step !== 4) {
      toast.warning('Your cart is empty');
      navigate('/shop-cart');
    }
  }, [isEmpty, navigate, step]);

  // Track begin checkout when page loads with items (intentionally runs once on mount)
  useEffect(() => {
    if (!isEmpty && cart?.items && cart.items.length > 0) {
      trackBeginCheckout(cart.items, subtotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load shipping address from localStorage on mount
  useEffect(() => {
    try {
      const savedAddress = localStorage.getItem('checkoutShippingAddress');
      if (savedAddress) {
        const parsedAddress = JSON.parse(savedAddress);
        setShippingAddress(parsedAddress);
      }
    } catch (error) {
      console.error('Error loading saved shipping address:', error);
      // If there's an error, just continue with empty form
    }
    // Mark initial load as complete so saving can begin
    initialLoadComplete.current = true;
  }, []);

  // Save shipping address to localStorage whenever it changes
  useEffect(() => {
    // Don't save until initial load is complete to avoid overwriting saved data
    if (!initialLoadComplete.current) {
      return;
    }
    // Only save if at least one field (besides country default) has been filled
    const hasUserData = Object.entries(shippingAddress).some(
      ([key, value]) => key !== 'country' && value && value !== ''
    );
    if (hasUserData) {
      try {
        localStorage.setItem('checkoutShippingAddress', JSON.stringify(shippingAddress));
      } catch (error) {
        console.error('Error saving shipping address:', error);
      }
    }
  }, [shippingAddress]);

  // Calculate discount amount based on discount type
  const calculateDiscountAmount = () => {
    if (!appliedDiscount) return 0;

    switch (appliedDiscount.type) {
      case 'PERCENTAGE':
        return Math.floor(subtotal * (appliedDiscount.value / 100));
      case 'FIXED':
        return Math.min(appliedDiscount.value, subtotal);
      case 'FREE_SHIPPING':
        // For free shipping, we don't reduce the subtotal but shipping is free
        return 0;
      default:
        return 0;
    }
  };

  // Calculate final total with discount
  const calculateFinalTotal = () => {
    const discountAmount = calculateDiscountAmount();
    return Math.max(0, subtotal - discountAmount);
  };

  // Handle discount applied
  const handleDiscountApplied = (discount) => {
    setAppliedDiscount(discount);
  };

  // Handle discount removed
  const handleDiscountRemoved = () => {
    setAppliedDiscount(null);
  };

  // Handle shipping form input changes
  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate shipping form
  const validateShippingForm = () => {
    const newErrors = {};

    if (!shippingAddress.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!shippingAddress.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!shippingAddress.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(shippingAddress.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!shippingAddress.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }
    if (!shippingAddress.address1.trim()) {
      newErrors.address1 = 'Address is required';
    }
    if (!shippingAddress.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!shippingAddress.zipCode.trim()) {
      newErrors.zipCode = 'Postal code is required';
    }
    if (!shippingAddress.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle shipping form submit
  const handleShippingSubmit = (e) => {
    e.preventDefault();
    if (validateShippingForm()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  // Handle payment form submit
  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    setStep(3);
    window.scrollTo(0, 0);
  };

  // Handle gift checkbox toggle
  const handleGiftToggle = (e) => {
    const checked = e.target.checked;
    setIsGift(checked);

    // Clear message and errors when unchecking
    if (!checked) {
      setGiftMessage('');
      setGiftMessageError('');
    }
  };

  // Handle gift message input
  const handleGiftMessageChange = (e) => {
    const value = e.target.value;

    // Enforce character limit
    if (value.length <= 500) {
      setGiftMessage(value);
      setGiftMessageError('');
    } else {
      setGiftMessageError('Gift message cannot exceed 500 characters');
    }
  };

  // Sanitize gift message to prevent XSS
  const sanitizeGiftMessage = (message) => {
    if (!message) return null;

    // Remove HTML tags
    const cleaned = message.replace(/<[^>]*>/g, '');

    // Trim whitespace
    return cleaned.trim();
  };

  // Handle place order
  /**
   * Handle successful payment (PaymentIntent flow)
   */
  const handlePaymentSuccess = async ({ paymentIntentId, orderId }) => {
    try {
      console.log('[Checkout] Payment successful, polling for order status...');

      // Poll for order status to confirm payment
      const result = await stripeService.pollOrderStatus(orderId, {
        maxAttempts: 30,
        interval: 2000,
      });

      if (result.success) {
        // Payment confirmed - clear cart and redirect
        await clearCart();
        toast.success('Payment successful! Your order is confirmed.');
        navigate(`/checkout/success?orderId=${orderId}`);
      } else {
        throw new Error(result.error || 'Payment failed');
      }
    } catch (error) {
      console.error('[Checkout] Payment status check failed:', error);
      toast.error('Unable to confirm payment. Please check your order status.');

      // Redirect to success page anyway (they can check status there)
      navigate(`/checkout/success?orderId=${orderId}`);
    }
  };

  /**
   * Handle payment error (PaymentIntent flow)
   */
  const handlePaymentError = (error) => {
    console.error('[Checkout] Payment error:', error);
    // Error already shown in CheckoutForm component
    // User can retry from the same page
  };

  /**
   * Handle back from payment step
   */
  const handleBackFromPayment = () => {
    setStep(3); // Go back to review
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    try {
      // Refresh cart from backend to ensure it exists and is up-to-date
      const cartResult = await fetchCart();
      if (!cartResult.success || !cartResult.cart?.items?.length) {
        toast.error('Your cart is empty or no longer exists. Please add items to your cart.');
        setIsProcessing(false);
        navigate('/shop-cart');
        return;
      }

      // Use refreshed cart data to build order
      const freshCart = cartResult.cart;

      // Build order data according to backend API spec
      const orderData = {
        // Items only need productId and quantity
        items: freshCart.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        // Shipping address - backend expects specific field names
        shippingAddress: {
          firstName: shippingAddress.firstName,
          lastName: shippingAddress.lastName,
          addressLine1: shippingAddress.address1,
          addressLine2: shippingAddress.address2 || '',
          city: shippingAddress.city,
          state: shippingAddress.state || '',
          postalCode: shippingAddress.zipCode,
          country: shippingAddress.country,
          phoneNumber: shippingAddress.phone || ''
        },
        billingAddress: {
          sameAsShipping: billingAddressSame
        },
        paymentMethod,
        email: shippingAddress.email,
        // Gift order options
        giftOrder: {
          isGift: isGift,
          giftMessage: isGift ? sanitizeGiftMessage(giftMessage) : null
        },
        // Include discount code if applied
        ...(appliedDiscount && { discountCode: appliedDiscount.code })
      };

      // Debug: log the order payload being sent
      console.log('[Checkout] Sending order data:', JSON.stringify(orderData, null, 2));

      const response = await orderService.createOrder(orderData);

      if (response.success) {
        // Clear saved shipping address from localStorage
        localStorage.removeItem('checkoutShippingAddress');

        // Store created order for PaymentIntent flow
        setCreatedOrder(response.data);

        // Feature flag: Check if using new Stripe PaymentIntent flow
        const usePaymentIntent = featureFlags.useStripePaymentIntent();

        if (usePaymentIntent && paymentMethod === 'stripe_card') {
          // New PaymentIntent flow - go to step 4 (embedded payment)
          console.log('[Checkout] Using PaymentIntent flow, moving to step 4');
          setStep(4);
          return;
        }

        // Old flow: Check if payment redirect URL is provided (PayPal/Stripe)
        if (response.data.paymentUrl) {
          // Redirect to payment provider (PayPal/Stripe) to complete payment
          toast.info('Redirecting to payment...');
          window.location.href = response.data.paymentUrl;
          return;
        }

        // No payment URL - order complete (e.g., free order or pay later)
        await clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-confirmation/${response.data.id}`);
      } else {
        throw new Error(response.error?.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('[Checkout] Place order error:', error);

      // Handle 404 error - cart doesn't exist on backend
      if (error.response?.status === 404 || error.message?.includes('404')) {
        toast.error('Your cart session has expired. Please add items to your cart again.');
        setIsProcessing(false);
        navigate('/shop-cart');
        return;
      }

      toast.error(error.message || 'Failed to place order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Initialize scripts
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Checkout] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* Checkout Section */}
      <div className="about_content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-lg-left wow slideInUp" data-wow-duration="2s">
              <h1 className="heading">Checkout</h1>
              <p className="para_text w-100 w-lg-75">Complete your order in just a few simple steps.</p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="row mt-5 mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center">
                <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">Shipping</div>
                </div>
                <div className={`step-line ${step >= 2 ? 'active' : ''}`}></div>
                <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">Payment</div>
                </div>
                <div className={`step-line ${step >= 3 ? 'active' : ''}`}></div>
                <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
                  <div className="step-number">3</div>
                  <div className="step-label">Review</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-5">
            {/* Main Content */}
            <div className="col-lg-8">
              {/* Step 1: Shipping Address */}
              {step === 1 && (
                <div className="checkout-step wow fadeIn" data-wow-duration="1s">
                  <h3 className="mb-4">Shipping Address</h3>
                  <form onSubmit={handleShippingSubmit}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                          id="firstName"
                          name="firstName"
                          value={shippingAddress.firstName}
                          onChange={handleShippingChange}
                        />
                        {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                          id="lastName"
                          name="lastName"
                          value={shippingAddress.lastName}
                          onChange={handleShippingChange}
                        />
                        {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                          id="email"
                          name="email"
                          value={shippingAddress.email}
                          onChange={handleShippingChange}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="phone">Phone *</label>
                        <input
                          type="tel"
                          className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                          id="phone"
                          name="phone"
                          value={shippingAddress.phone}
                          onChange={handleShippingChange}
                        />
                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="address1">Address Line 1 *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.address1 ? 'is-invalid' : ''}`}
                        id="address1"
                        name="address1"
                        value={shippingAddress.address1}
                        onChange={handleShippingChange}
                      />
                      {errors.address1 && <div className="invalid-feedback">{errors.address1}</div>}
                    </div>

                    <div className="mb-3">
                      <label htmlFor="address2">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control"
                        id="address2"
                        name="address2"
                        value={shippingAddress.address2}
                        onChange={handleShippingChange}
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                          id="city"
                          name="city"
                          value={shippingAddress.city}
                          onChange={handleShippingChange}
                        />
                        {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="state">State/County</label>
                        <input
                          type="text"
                          className="form-control"
                          id="state"
                          name="state"
                          value={shippingAddress.state}
                          onChange={handleShippingChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="zipCode">Postal Code *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.zipCode ? 'is-invalid' : ''}`}
                          id="zipCode"
                          name="zipCode"
                          value={shippingAddress.zipCode}
                          onChange={handleShippingChange}
                        />
                        {errors.zipCode && <div className="invalid-feedback">{errors.zipCode}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="country">Country *</label>
                        <select
                          className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                          id="country"
                          name="country"
                          value={shippingAddress.country}
                          onChange={handleShippingChange}
                        >
                          <option value="UK">United Kingdom</option>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="AU">Australia</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                      </div>
                    </div>

                    <div className="mt-4">
                      <button type="submit" className="btn pink-gradient-btn-into-black">
                        Continue to Payment
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 2: Payment Method */}
              {step === 2 && (
                <div className="checkout-step wow fadeIn" data-wow-duration="1s">
                  <h3 className="mb-4">Payment Method</h3>
                  <form onSubmit={handlePaymentSubmit}>
                    <div className="payment-methods">
                      <div className="payment-method mb-3">
                        <input
                          type="radio"
                          id="stripe_card"
                          name="paymentMethod"
                          value="stripe_card"
                          checked={paymentMethod === 'stripe_card'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <label htmlFor="stripe_card" className="ml-2">
                          <i className="fas fa-credit-card mr-2"></i>
                          Credit/Debit Card (Stripe)
                        </label>
                      </div>

                      <div className="payment-method mb-3">
                        <input
                          type="radio"
                          id="paypal"
                          name="paymentMethod"
                          value="paypal"
                          checked={paymentMethod === 'paypal'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        />
                        <label htmlFor="paypal" className="ml-2">
                          <i className="fab fa-paypal mr-2"></i>
                          PayPal
                        </label>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="form-check mb-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="billingAddressSame"
                          checked={billingAddressSame}
                          onChange={(e) => setBillingAddressSame(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="billingAddressSame">
                          Billing address is same as shipping address
                        </label>
                      </div>
                    </div>

                    {/* Gift Order Section */}
                    <div className="gift-order-section mt-4 mb-4">
                      <h5 className="section-heading mb-3">Gift Options</h5>

                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="giftCheckbox"
                          className="form-check-input"
                          checked={isGift}
                          onChange={handleGiftToggle}
                        />
                        <label htmlFor="giftCheckbox" className="form-check-label">
                          This order is a gift
                        </label>
                      </div>

                      {/* Gift message textarea - shown when gift is checked */}
                      {isGift && (
                        <div className="gift-message-container mt-3 animate-fadeIn">
                          <label htmlFor="giftMessage" className="form-label">
                            Gift Message (Optional)
                            <span className="text-muted small ms-2">
                              {giftMessage.length}/500 characters
                            </span>
                          </label>
                          <textarea
                            id="giftMessage"
                            className={`form-control ${giftMessageError ? 'is-invalid' : ''}`}
                            rows="4"
                            maxLength="500"
                            placeholder="Enter a personal message for the recipient..."
                            value={giftMessage}
                            onChange={handleGiftMessageChange}
                            aria-describedby="giftMessageHelp"
                          />
                          <small id="giftMessageHelp" className="form-text text-muted">
                            Your message will be included with the order.
                          </small>
                          {giftMessageError && (
                            <div className="invalid-feedback d-block">
                              {giftMessageError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setStep(1)}
                      >
                        Back
                      </button>
                      <button type="submit" className="btn pink-gradient-btn-into-black ml-2">
                        Review Order
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 3: Review Order */}
              {step === 3 && (
                <div className="checkout-step wow fadeIn" data-wow-duration="1s">
                  <h3 className="mb-4">Review Your Order</h3>

                  {/* Shipping Address Summary */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title">Shipping Address</h5>
                      <p className="mb-0">
                        {shippingAddress.firstName} {shippingAddress.lastName}<br />
                        {shippingAddress.address1}<br />
                        {shippingAddress.address2 && <>{shippingAddress.address2}<br /></>}
                        {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}<br />
                        {shippingAddress.country}<br />
                        Email: {shippingAddress.email}<br />
                        Phone: {shippingAddress.phone}
                      </p>
                      <button
                        className="btn btn-link p-0 mt-2"
                        onClick={() => setStep(1)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Payment Method Summary */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title">Payment Method</h5>
                      <p className="mb-0">
                        {paymentMethod === 'stripe_card' ? (
                          <><i className="fas fa-credit-card mr-2"></i>Credit/Debit Card (Stripe)</>
                        ) : (
                          <><i className="fab fa-paypal mr-2"></i>PayPal</>
                        )}
                      </p>
                      <button
                        className="btn btn-link p-0 mt-2"
                        onClick={() => setStep(2)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>

                  {/* Gift Order Summary */}
                  {isGift && (
                    <div className="card mb-4 border-success">
                      <div className="card-body">
                        <h5 className="card-title">
                          <i className="fas fa-gift me-2 text-success"></i>
                          Gift Order
                        </h5>
                        {giftMessage ? (
                          <>
                            <p className="mb-0"><strong>Gift Message:</strong></p>
                            <p className="mb-0 text-muted fst-italic">"{giftMessage}"</p>
                          </>
                        ) : (
                          <p className="mb-0 text-muted">This order will be sent as a gift (no message included).</p>
                        )}
                        <button
                          className="btn btn-link p-0 mt-2"
                          onClick={() => setStep(2)}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <h5 className="card-title">Order Items</h5>
                      {cart?.items?.map((item) => (
                        <div key={item.id} className="d-flex justify-content-between align-items-center mb-3">
                          <div className="d-flex align-items-center">
                            {item.product?.images?.[0]?.url && (
                              <img
                                src={item.product.images[0].url}
                                alt={item.product?.name}
                                style={{ width: '60px', height: '60px', objectFit: 'cover', marginRight: '15px' }}
                              />
                            )}
                            <div>
                              <h6 className="mb-0">{item.product?.name}</h6>
                              <small className="text-muted">Quantity: {item.quantity}</small>
                            </div>
                          </div>
                          <div className="text-right">
                            <strong>{formatPrice(item.price * item.quantity)}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Logged In Status - Show if authenticated */}
                  {isAuthenticated && user && (
                    <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
                      <i className="fas fa-check-circle mr-2"></i>
                      <span>Logged in as <strong>{user.firstName || user.email}</strong>. You're ready to place your order!</span>
                    </div>
                  )}

                  {/* Login Prompt - Show if not authenticated */}
                  {!isAuthenticated && (
                    <div className="card mb-4 border-warning">
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <i className="fas fa-user-lock mr-3" style={{ fontSize: '24px', color: '#e67e22' }}></i>
                          <div>
                            <h5 className="card-title mb-1">Login Required</h5>
                            <p className="mb-0 text-muted">Please log in or create an account to complete your order.</p>
                          </div>
                        </div>
                        <p className="small text-muted mb-3">
                          Creating an account allows you to track your orders, view order history, and enjoy a faster checkout experience.
                        </p>
                        <div className="d-flex gap-2">
                          <Link
                            to="/login"
                            state={{ from: '/checkout', message: 'Please log in to complete your order' }}
                            className="btn pink-gradient-btn-into-black"
                          >
                            Log In
                          </Link>
                          <Link
                            to="/register"
                            state={{ from: '/checkout', message: 'Create an account to complete your order' }}
                            className="btn btn-outline-secondary ml-2"
                          >
                            Create Account
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </button>
                    {isAuthenticated ? (
                      <button
                        type="button"
                        className="btn pink-gradient-btn-into-black ml-2"
                        onClick={handlePlaceOrder}
                        disabled={isProcessing}
                      >
                        {isProcessing ? 'Processing...' : 'Place Order'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary ml-2"
                        disabled
                        title="Please log in to place your order"
                      >
                        Place Order
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Payment (PaymentIntent flow only) */}
              {step === 4 && createdOrder && (
                <div className="checkout-step wow fadeIn" data-wow-duration="1s">
                  <PaymentStep
                    order={createdOrder}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onBack={handleBackFromPayment}
                  />
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="col-lg-4">
              <div className="card sticky-top" style={{ top: '100px', zIndex: 1000 }}>
                <div className="card-body">
                  <h5 className="card-title mb-4">Order Summary</h5>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal ({itemCount} items)</span>
                    <span>{formatPrice(cart?.summary?.subtotal || subtotal)}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span>Shipping</span>
                    <span>
                      {appliedDiscount?.type === 'FREE_SHIPPING' ? (
                        <span className="text-success">FREE (Discount)</span>
                      ) : cart?.summary?.hasFreeShipping ? (
                        <span className="text-success">FREE</span>
                      ) : (
                        formatPrice(cart?.summary?.shipping || 0)
                      )}
                    </span>
                  </div>

                  {/* Show tax if present */}
                  {cart?.summary?.tax > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Tax (8%)</span>
                      <span>{formatPrice(cart.summary.tax)}</span>
                    </div>
                  )}

                  {/* Show discount if applied */}
                  {appliedDiscount && calculateDiscountAmount() > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Discount ({appliedDiscount.code})</span>
                      <span>-{formatPrice(calculateDiscountAmount())}</span>
                    </div>
                  )}

                  <hr />

                  <div className="d-flex justify-content-between mb-3">
                    <strong>Total</strong>
                    <strong className="text-primary">{formatPrice(cart?.summary?.total || calculateFinalTotal())}</strong>
                  </div>

                  {/* Discount Code Input */}
                  <DiscountCodeInput
                    subtotal={subtotal}
                    onDiscountApplied={handleDiscountApplied}
                    onDiscountRemoved={handleDiscountRemoved}
                    appliedDiscount={appliedDiscount}
                  />

                  <div className="small text-muted">
                    <p className="mb-2">
                      <i className="fas fa-lock mr-2"></i>
                      Secure checkout
                    </p>
                    {cart?.summary?.hasFreeShipping ? (
                      <p className="mb-0 text-success">
                        <i className="fas fa-truck mr-2"></i>
                        Free shipping applied!
                      </p>
                    ) : cart?.summary?.freeShippingThreshold ? (
                      <p className="mb-0">
                        <i className="fas fa-truck mr-2"></i>
                        Add {formatPrice(cart.summary.freeShippingThreshold - (cart?.summary?.subtotal || 0))} more for free shipping
                      </p>
                    ) : (
                      <p className="mb-0">
                        <i className="fas fa-truck mr-2"></i>
                        Fast UK delivery
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Checkout;
